import React, { createContext, useContext, useState, useEffect } from 'react';

const HubStateContext = createContext();

export const useHubState = () => {
  const context = useContext(HubStateContext);
  if (!context) {
    throw new Error('useHubState must be used within HubStateProvider');
  }
  return context;
};

export const HubStateProvider = ({ children, currentUser }) => {
  const [hubState, setHubState] = useState({
    firstVisit: null,
    phaseUnlocks: {
      phase1: null,
      phase2: null,
      phase3: null
    },
    interactions: {
      viewsVisited: [],
      analysesRun: []
    },
    preferences: {
      phaseBlocksCollapsed: true // Default to collapsed
    }
  });

  // Generate storage key based on user ID
  const getStorageKey = () => {
    const userId = currentUser?.id || 'guest';
    return `hubState_${userId}`;
  };

  // Load state from localStorage on mount
  useEffect(() => {
    const storageKey = getStorageKey();
    const savedState = localStorage.getItem(storageKey);

    if (savedState) {
      try {
        const parsed = JSON.parse(savedState);
        setHubState(parsed);
      } catch (error) {
        console.error('Error parsing hub state from localStorage:', error);
      }
    } else {
      // First visit - initialize with current timestamp
      const initialState = {
        firstVisit: new Date().toISOString(),
        phaseUnlocks: {
          phase1: null,
          phase2: null,
          phase3: null
        },
        interactions: {
          viewsVisited: [],
          analysesRun: []
        },
        preferences: {
          phaseBlocksCollapsed: true // Default to collapsed
        }
      };
      setHubState(initialState);
      localStorage.setItem(storageKey, JSON.stringify(initialState));
    }
  }, [currentUser?.id]);

  // Save state to localStorage whenever it changes
  const updateHubState = (updates) => {
    const newState = { ...hubState, ...updates };
    setHubState(newState);

    const storageKey = getStorageKey();
    localStorage.setItem(storageKey, JSON.stringify(newState));
  };

  // Record phase unlock
  const recordPhaseUnlock = (phase) => {
    if (!hubState.phaseUnlocks[phase]) {
      updateHubState({
        phaseUnlocks: {
          ...hubState.phaseUnlocks,
          [phase]: new Date().toISOString()
        }
      });
    }
  };

  // Record view visit
  const recordViewVisit = (viewName) => {
    if (!hubState.interactions.viewsVisited.includes(viewName)) {
      updateHubState({
        interactions: {
          ...hubState.interactions,
          viewsVisited: [...hubState.interactions.viewsVisited, viewName]
        }
      });
    }
  };

  // Record analysis run
  const recordAnalysisRun = (analysisType) => {
    const timestamp = new Date().toISOString();
    const record = `${analysisType}_${timestamp}`;

    updateHubState({
      interactions: {
        ...hubState.interactions,
        analysesRun: [...hubState.interactions.analysesRun, record]
      }
    });
  };

  // Toggle phase blocks collapsed state
  const togglePhaseBlocks = () => {
    updateHubState({
      preferences: {
        ...hubState.preferences,
        phaseBlocksCollapsed: !hubState.preferences.phaseBlocksCollapsed
      }
    });
  };

  // Helper: Should show phase blocks expanded
  const shouldShowPhaseBlocksExpanded = (portfolioStatus) => {
    // Collapsed by default - only show if user has explicitly expanded
    return !hubState.preferences.phaseBlocksCollapsed;
  };

  // Helper: Should show phase unlock celebration
  const shouldShowPhaseUnlockCelebration = (phase) => {
    const unlockDate = hubState.phaseUnlocks[phase];
    if (!unlockDate) return false;

    const now = new Date();
    const hoursSinceUnlock = (now - new Date(unlockDate)) / (1000 * 60 * 60);

    // Show celebration for first 24 hours after unlock
    return hoursSinceUnlock < 24;
  };

  // Helper: Is this user's first time in a specific view
  const isFirstTimeInView = (viewName) => {
    return !hubState.interactions.viewsVisited.includes(viewName);
  };

  // Helper: Has user run a specific analysis type
  const hasRunAnalysis = (analysisType) => {
    return hubState.interactions.analysesRun.some(record =>
      record.startsWith(analysisType)
    );
  };

  const value = {
    hubState,
    recordPhaseUnlock,
    recordViewVisit,
    recordAnalysisRun,
    togglePhaseBlocks,
    shouldShowPhaseBlocksExpanded,
    shouldShowPhaseUnlockCelebration,
    isFirstTimeInView,
    hasRunAnalysis
  };

  return (
    <HubStateContext.Provider value={value}>
      {children}
    </HubStateContext.Provider>
  );
};

export default HubStateContext;
